import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { reduxForm } from 'redux-form';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import classnames from 'classnames';

import ORTypography from 'components/ORTypography/ORTypography';
import ORTooltip from 'components/ORTooltip/ORTooltip';
import ServiceIcon from 'components/ServiceIcon/ServiceIcon';

const propTypes = {
  serviceName: PropTypes.string.isRequired,
  isServiceBeta: PropTypes.bool.isRequired,
  versionNamesByService: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedService: PropTypes.string,
  className: PropTypes.string,
  change: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  trackButtonClick: PropTypes.func.isRequired,
  orgIsAwsOnly: PropTypes.bool.isRequired,
};

const defaultProps = {
  selectedService: '',
  className: '',
};

const useStyles = makeStyles((theme) => ({
  tooltip: {
    maxWidth: 125,
  },
  serviceSelector: {
    border: (props) =>
      props.selected
        ? `2px solid ${theme.customColors.teal}`
        : `2px solid ${theme.customColors.mediumGrey}`,
    width: '200px',
    height: '60px',
    position: 'relative',
    cursor: 'pointer',
    display: 'inline-block',
  },
  serviceIcon: {
    height: '95%',
    left: '1%',
    width: 'unset',
    opacity: 0.5,
    position: 'absolute',
    top: '50%',
    transform: 'translate(0,-50%)',
  },
  serviceName: {
    top: '50%',
    position: 'absolute',
    transform: 'translate(0,-50%)',
    left: '2rem',
    fontWeight: 500,
  },
  betaText: {
    color: theme.palette.primary.main,
    fontStyle: 'italic',
    fontSize: '0.7857142857rem',
    fontWeight: 'bold',
  },
  versionMenuButton: {
    position: 'absolute',
    right: 0,
    padding: 0,
  },
  versionMenuButtonLabel: {
    fontFamily: ['"Libre Franklin"', 'sans-serif'],
    fontSize: '.857rem',
  },
  isSelected: {
    backgroundColor: theme.customColors.coolGrey,
  },
  launchButton: {
    position: 'absolute',
    top: '50%',
    right: '3%',
    transform: 'translate(0,-50%)',
    backgroundColor: theme.customColors.sky,
    color: theme.customColors.black,
    padding: theme.spacing(1),
    borderRadius: 3,
    boxShadow: `1px 1px 4px ${theme.customColors.black}`,

    '&:hover': {
      backgroundColor: theme.customColors.sky,
    },
  },
}));

const tooltipText = {
  Elasticsearch: 'An Open Source Distributed, RESTful Search & Analytics Engine.',
  CockroachDB: 'A relational Database for next-generation, cloud-native applications.',
  PostgreSQL: 'A powerful, open source object-relational database system.',
  Redis:
    'Open source (BSD licensed), in-memory data structure store, used as a database, cache and message broker.',
  MongoDB:
    'A document database with the scalability and flexibility that you want with the querying and indexing that you need.',
  TimescaleDB:
    'A time-series SQL database providing fast analytics, scalability, with automated data management on a proven storage engine.',
  FoundationDB:
    'An open-source, multi-model, easily scalable and fault tolerant distributed database.',
};

export function UnstyledServiceSelector({
  serviceName,
  isServiceBeta,
  versionNamesByService,
  selectedService,
  className,
  change,
  initialize,
  trackButtonClick,
  orgIsAwsOnly,
}) {
  const classes = useStyles({ selected: serviceName === selectedService });
  const [anchorEl, setAnchorEl] = React.useState(null);

  const [currentVersionDisplay, setCurrentVersionDisplay] = React.useState(0);

  const handleServiceNameClick = () => {
    // Don't reset the whole form on service selection.
    // Initialize the form with the service & version
    // Reset the rest of step one and all of step two
    // The second object keeps the steps three & four values if they exist
    initialize(
      {
        cloudProvider: orgIsAwsOnly ? 'AWS' : undefined,
        region: undefined,
        flavor: undefined,
        addons: undefined,
        plan: undefined,
        version: versionNamesByService[currentVersionDisplay],
        service: serviceName,
      },
      {
        updateUnregisteredFields: true,
        keepDirty: true,
      },
    );
  };

  const handleVersionMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleVersionMenuItemClick = (event) => {
    event.stopPropagation();
    change('version', event.target.textContent);
    setCurrentVersionDisplay(Number(event.target.getAttribute('index')));
    setAnchorEl(null);
  };

  /* istanbul ignore next */
  const handleVersionMenuClose = () => {
    setAnchorEl(null);
  };

  const versionMenuItems = versionNamesByService.map((version, index) => (
    <MenuItem
      key={version}
      onClick={(e) => {
        handleVersionMenuItemClick(e);
        trackButtonClick(selectedService.concat(' ', version));
      }}
      index={index}
    >
      {version}
    </MenuItem>
  ));

  const actionIcon = (
    <IconButton
      className={classes.versionMenuButton}
      classes={{ label: classes.versionMenuButtonLabel }}
      onClick={handleVersionMenuClick}
    >
      {selectedService === serviceName
        ? `v. ${versionNamesByService[currentVersionDisplay] || ''}`
        : null}
      <ExpandMoreIcon />
    </IconButton>
  );

  const selectedClass = serviceName === selectedService && classes.isSelected;

  return (
    <ORTooltip classes={{ tooltip: classes.tooltip }} title={tooltipText[serviceName] || ''} arrow>
      <Paper
        className={classnames(classes.serviceSelector, className, selectedClass)}
        onClick={handleServiceNameClick}
        elevation={0}
        component="div"
      >
        {' '}
        <ServiceIcon service={serviceName} isAlpha={false} className={classes.serviceIcon} />
        <ORTypography className={classes.serviceName}>
          {serviceName} {isServiceBeta && <span className={classes.betaText}>BETA</span>}
        </ORTypography>
        {actionIcon}
        <Menu
          keepMounted
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleVersionMenuClose}
        >
          {versionMenuItems}
        </Menu>
      </Paper>
    </ORTooltip>
  );
}

UnstyledServiceSelector.propTypes = propTypes;
UnstyledServiceSelector.defaultProps = defaultProps;

export default reduxForm({
  form: 'create',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(UnstyledServiceSelector);
